import React, { useEffect, useState } from 'react';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import {
  getNumberBeforeHyphen,
  getSections,
  proposalDrawerInitialValues,
} from './content';
import { useLazyQuery, useMutation } from '@apollo/client';
import { IProposalDrawerProps, IProposalDrawerValues, IProposalLovs } from '.';
import {
  createProposal,
  getActiveAssignedBPPlanList,
  getAssignedBPList,
  getAssignedLinesListQuery,
} from './queries';
import { useAppSelector } from '@redux/hooks';
import {
  extractAssignedBPWithPagination,
  extractLines,
  extractPlanAssignBPPlan_Id,
  extractPlans,
  extractSublines,
  transformToShare,
} from './utils';
import { isEmpty, isGuid } from '@utils/validationUtils';
import { cloneDeep, isArray } from 'lodash';
import { toast } from 'react-toastify';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { getError } from '@utils/graph-utils';
import { useNavigate } from 'react-router-dom';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { ErrorsOf, IPagination, TouchedOf } from '@models';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { useResetKey } from '@hooks/useResetKey';

const ProposalDrawer: React.FC<IProposalDrawerProps> = ({
  open,
  onSuccess,
  onClose,
}) => {
  const navigate = useNavigate();
  const [formRefreshKey, refreshForm] = useResetKey('formRefreshKey');
  const user = useAppSelector((state) => state.user);
  const businessUserId = user.info.businessUser_Id;
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [planCurrencies, setPlanCurrencies] = useState<Record<string, string>>(
    {}
  );
  const [lists, setLists] = useState<IProposalLovs>({
    line: {},
    subline: {},
    plan: {},
    assignedPlan: {},
    isGroup: {
      Yes: 'Yes',
      No: 'No',
    },
  });
  const [values, setValues] = useState<IProposalDrawerValues>();
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    getSections(values, lists, user.info.businessUser_AllowGroupPolicy)
  );

  const [getAssignedBPListLazy, { loading: bpLoading }] =
    useLazyQuery(getAssignedBPList);

  const [getActiveAssignedBPPlanListLazy, { loading: planListLoading }] =
    useLazyQuery(getActiveAssignedBPPlanList);

  const [getAssignedLinesList, { loading: lineListLoading }] = useLazyQuery(
    getAssignedLinesListQuery
  );

  // const [getAssignedPlansList, { loading: planListLoading }] = useLazyQuery(
  //   getAssignedPlansListQuery
  // );

  const [createProposalAction] = useMutation(createProposal);

  const initialize = async () => {
    setValues(proposalDrawerInitialValues);
    setInputsForm(
      getSections(
        proposalDrawerInitialValues,
        lists,
        user.info.businessUser_AllowGroupPolicy
      )
    );
  };

  useEffect(() => {
    initialize();
  }, []);

  const handleLineSelection = async (selectedOption: string) => {
    if (!isEmpty(selectedOption)) {
      const newData = await getActiveAssignedBPPlanListLazy({
        variables: {
          currentPage: 1,
          currentPageSize: 10,
          selectedBusinessUserID: businessUserId,
          selectedBusinessPartnerID:
            values.businessPartnerRepeater[0].businessPartner,
          selectedLineID: selectedOption,
        },
        errorPolicy: 'all',
      });
      return extractSublines(newData.data);
    }
    return {} as Record<string, string>;
  };

  const handleSublineSelection = async (selectedOption: string) => {
    if (!isEmpty(selectedOption)) {
      const newData = await getActiveAssignedBPPlanListLazy({
        variables: {
          currentPage: 1,
          currentPageSize: 10,
          selectedBusinessUserID: businessUserId,
          selectedBusinessPartnerID:
            values.businessPartnerRepeater[0].businessPartner,
          selectedLineID: isArray(values.line) ? values.line[0] : values.line,
          selectedSublineID: selectedOption,
        },
        errorPolicy: 'all',
      });
      const newPlanList = extractPlans(newData.data);

      setPlanCurrencies(newPlanList.planCurrencies);
      return {
        plans: newPlanList.plans,
        assignedPlans: extractPlanAssignBPPlan_Id(newData.data),
      };
    }
    return {
      plans: {},
      assignedPlans: {},
    };
  };

  const handleBPSelection = async (selectedOption: string) => {
    if (isGuid(selectedOption)) {
      const { data } = await getAssignedLinesList({
        variables: {
          selectedBusinessUserID: businessUserId,
          selectedBusinessPartnerID: [selectedOption],
        },
        errorPolicy: 'all',
      });

      return extractLines(data);
    }
    return {};
  };

  const onCustomChange = async (
    fieldName: keyof IProposalDrawerValues,
    newValue: any,
    sectionIndex: number,
    allValues: IProposalDrawerValues,
    errors: ErrorsOf<IProposalDrawerValues>,
    touched: TouchedOf<IProposalDrawerValues>,
    additionalProperties?: {
      subFieldName?: string;
      index?: number;
    }
  ) => {
    const newLists = cloneDeep(lists);
    allValues[fieldName] = newValue;

    if (
      fieldName === 'businessPartnerRepeater' &&
      additionalProperties.subFieldName === 'businessPartner' &&
      additionalProperties.index === 0
    ) {
      const businessPartnerId = newValue[0].businessPartner;
      const newLines = await handleBPSelection(businessPartnerId);
      newLists.line = newLines;
      newLists.subline = {};
      newLists.plan = {};
      allValues.line = '';
      allValues.subline = '';
      allValues.plan = '';
    } else if (fieldName === 'line') {
      const newSublines = await handleLineSelection(newValue);
      newLists.subline = newSublines;
      allValues.subline = '';

      allValues.plan = '';
      newLists.plan = {};
    } else if (fieldName === 'subline') {
      const { plans, assignedPlans } = await handleSublineSelection(newValue);
      newLists.plan = plans;
      newLists.assignedPlan = assignedPlans;
      allValues.plan = '';
    } else if (fieldName === 'plan') {
      allValues.policyCurrency = planCurrencies[newValue] || '';
    }
    setValues(allValues);
    setLists(newLists);
    setInputsForm(
      getSections(allValues, newLists, user.info.businessUser_AllowGroupPolicy)
    );
  };

  const handleSearch = (
    fieldName: string,
    value: string,
    pagination: IPagination
  ) => {
    return getAssignedBPListLazy({
      variables: {
        selectedBusinessUserID: businessUserId,
        searchKeyword: value,
        pagination,
      },
    }).then((result) => {
      return {
        options: extractAssignedBPWithPagination(result.data),
        totalCount:
          result.data.SalesforceManagement.queries.getAssignedBPList.paging
            .totalCount,
      };
    });
  };

  const onSubmit = async (data: IProposalDrawerValues) => {
    setSubmitButtonState('loading'), setFormDisabled(true);

    try {
      const variables = {
        lineID: data.line,
        sublineID: data.subline,
        planID: data.plan,
        primaryBP: data.businessPartnerRepeater[0].businessPartner,
        primaryPercentage: transformToShare(
          data.businessPartnerRepeater[0].share
        ),
        ...(data.businessPartnerRepeater[1]?.businessPartner && {
          secondaryBP: data.businessPartnerRepeater[1].businessPartner,
          secondaryPercentage: transformToShare(
            data.businessPartnerRepeater[1].share
          ),
        }),
        ...(data.businessPartnerRepeater[2]?.businessPartner && {
          additionalBP: data.businessPartnerRepeater[2].businessPartner,
          additionalPercentage: transformToShare(
            data.businessPartnerRepeater[2].share
          ),
        }),
        policyCurrency: data.policyCurrency,
        selectedAssignedBPPlan: lists['assignedPlan'][data.plan],
        isGroup: ['4', '50', '51', '3'].includes(
          getNumberBeforeHyphen(lists?.line[data?.line])?.toString()
        )
          ? data.isGroup === 'Yes'
          : false,
      };
      const result = await createProposalAction({
        variables,
        errorPolicy: 'all',
      });

      if (isEmpty(result.errors)) {
        toast.success(
          <ToastSuccessMessage>
            Proposal successfully created
          </ToastSuccessMessage>
        );
        const proposalId = result.data.production.actions.createProposal.id;
        setTimeout(() => {
          setSubmitButtonState('success');
          if (proposalId) {
            navigate(`/production/proposal/` + proposalId);
          } else {
            onSuccess();
            onClose();
            setFormDisabled(false);
          }
        }, 500);
      } else {
        setSubmitButtonState(undefined);
        setFormDisabled(false);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    } catch (error) {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    } finally {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
    }
  };

  return (
    <DynamicFormDrawer
      key={formRefreshKey}
      title={'New Proposal'}
      onClose={() => onClose()}
      isOpen={open}
      onSubmit={onSubmit}
      onChange={onCustomChange}
      submitButtonState={submitButtonState}
      loadingFields={{
        businessPartnerRepeater: {
          businessPartner: bpLoading,
        },
        line: lineListLoading,
        subline: planListLoading,
        plan: planListLoading,
      }}
      disableForm={formDisabled}
      sections={inputsForm}
      onAutocompleteSearch={handleSearch}
    />
  );
};

export default ProposalDrawer;
